import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import NukaCarousel from '@r1-frontend/ui-react/components/NukaCarousel';
import { ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { TvSubscription } from '@r1-frontend/ui-react/organisms/TariffSubscription';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import SubscriptionsAdditionalInfoPopup from '~/src/features/full-buy/components/SubscriptionsPopup/SubscriptionsAdditionalInfoPopup';
import { selectSubscriptionPopup } from '~/src/features/full-buy/helpers/selectSubscriptionPopup';
import { selectSubscription, unSelectSubscription } from '~/src/features/full-buy/store/actions';
import getSubscriptionsWithConditions from '~/src/features/full-buy/store/selectors/getSubscriptionsWithConditions';

const initPopupData = {
    displayImage: '',
    title: '',
    description: [],
    isOpen: false,
};
type TSubscriptions = {
    displayImage: string,
    title: string,
    description: string[],
    isOpen: boolean,
}
export const SubscriptionsContainer = ({ className }: { className?: string }): JSX.Element => {
    const analytics = useAnalytics();

    const dispatch = useDispatch();
    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);
    const { subscriptions, selected, leftToSelect } = useSelector(getSubscriptionsWithConditions);
    const [selectedSubscription, setState] = useState<TSubscriptions>(initPopupData);

    const handleToggle = (id: number, title: string) => () => {
        const state = !selected.includes(id);
        const callback = state ? selectSubscription : unSelectSubscription;
        dispatch(callback(id, title, analytics.subscriptions));
    };

    const handleIconClick = (id: number) => () => {
        const subscription = subscriptions.find(s => s.serviceId === id);
        if (subscription) {
            setState(subscription ? selectSubscriptionPopup(subscription) : initPopupData);
        }
    };

    const handleClosePopup = () => {
        setState(initPopupData);
    };

    return <div className={className}>
        {isMobile
            ? (
                <ListContainer $mobile={8} $desktop={16} $direction={'column'}>
                    {subscriptions && subscriptions.map(subscription => (
                        <TvSubscription
                            key={`FullBuy-Subscription-${subscription.serviceId}`}
                            onToggle={handleToggle(subscription.serviceId, subscription.title)}
                            imagePath={subscription.images}
                            onIconClick={handleIconClick(subscription.serviceId)}
                            description={subscription.description}
                            disabled={!leftToSelect && !selected.includes(subscription.serviceId)}
                            checked={selected.includes(subscription.serviceId)}
                        >
                            {subscription.title}
                        </TvSubscription>
                    ))}
                </ListContainer>
            )
            : (
                <NukaCarousel
                    withoutDots
                    slideWidth={'304px'}
                    cellSpacing={16}
                >
                    {subscriptions && subscriptions.map(subscription => (
                        <TvSubscription
                            key={`FullBuy-Subscription-${subscription.serviceId}`}
                            onToggle={handleToggle(subscription.serviceId, subscription.title)}
                            imagePath={subscription.images}
                            onIconClick={handleIconClick(subscription.serviceId)}
                            description={subscription.description}
                            disabled={!leftToSelect && !selected.includes(subscription.serviceId)}
                            checked={selected.includes(subscription.serviceId)}
                        >
                            {subscription.title}
                        </TvSubscription>
                    ))}
                </NukaCarousel>
            )}
        <SubscriptionsAdditionalInfoPopup
            onClose={handleClosePopup}
            {...selectedSubscription}
        />
    </div>;
};

export default styled(SubscriptionsContainer)`
    @media (min-width: ${wideBreakpoints.mobile}) {
        ${TvSubscription} {
            min-height: 100%;
        }
    }
    
    ${ListContainer} {
        width: 100%;
    }
`;
